/**
 * Route tree of the application.
 *
 * The structure mirrors the three zones of the product, and each zone is a *layout route* so the guard decides once
 * for every address beneath it:
 *
 * - the public landing page and the verification link (the latter deliberately sits outside `RequireGuest`, because a
 *   signed-in user who clicks an old e-mail link must still be able to confirm the address);
 * - `/auth/*` behind {@link AuthLayout} and `RequireGuest`;
 * - the authenticated application behind `RequireAuth` and {@link AppShell}, with `/admin` additionally behind
 *   `RequireAdministrator`.
 *
 * Pages are loaded lazily: the sign-in screen must not pay for the settings screens, and the shell already renders a
 * skeleton as its `Suspense` fallback.
 *
 * One wizard address is a *redirect* rather than a screen (TASK-0021): `/wizard` used to open a questionnaire draft, and
 * it now hands over to `/wizard/framework`, the product's only way to start a prompt. A draft that already exists keeps
 * its own address, because browser history, bookmarks and pasted links are full of `/wizard/{sessionId}`.
 */

import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { AdminPage } from '@/features/admin/pages/AdminPage';
import { ForbiddenPage, NotFoundPage } from '@/features/common/pages/MessageScreens';
import {
  RequireAdministrator,
  RequireAuth,
  RequireGuest,
  RequireVerifiedIdentity,
} from '@/routes/guards';

const LandingPage = lazy(async () => {
  const module = await import('@/features/landing/pages/LandingPage');

  return { default: module.LandingPage };
});

const LoginPage = lazy(async () => {
  const module = await import('@/features/auth/pages/LoginPage');

  return { default: module.LoginPage };
});

const RegisterPage = lazy(async () => {
  const module = await import('@/features/auth/pages/RegisterPage');

  return { default: module.RegisterPage };
});

const ForgotPasswordPage = lazy(async () => {
  const module = await import('@/features/auth/pages/ForgotPasswordPage');

  return { default: module.ForgotPasswordPage };
});

const ResetPasswordPage = lazy(async () => {
  const module = await import('@/features/auth/pages/ResetPasswordPage');

  return { default: module.ResetPasswordPage };
});

const VerifyEmailPage = lazy(async () => {
  const module = await import('@/features/auth/pages/VerifyEmailPage');

  return { default: module.VerifyEmailPage };
});

const VerifyPage = lazy(async () => {
  const module = await import('@/features/auth/pages/VerifyPage');

  return { default: module.VerifyPage };
});

const ExternalCallbackPage = lazy(async () => {
  const module = await import('@/features/auth/pages/ExternalCallbackPage');

  return { default: module.ExternalCallbackPage };
});

/** The organisation authority's callback (TASK-0064): the SPA's own OIDC client completes the flow here. */
const OrganisationCallbackPage = lazy(async () => {
  const module = await import('@/features/auth/pages/OrganisationCallbackPage');

  return { default: module.OrganisationCallbackPage };
});

const DashboardPage = lazy(async () => {
  const module = await import('@/features/dashboard/pages/DashboardPage');

  return { default: module.DashboardPage };
});

const SettingsPage = lazy(async () => {
  const module = await import('@/features/settings/pages/SettingsPage');

  return { default: module.SettingsPage };
});

const WizardPage = lazy(async () => {
  const module = await import('@/features/wizard/pages/WizardPage');

  return { default: module.WizardPage };
});

const FrameworkWizardPage = lazy(async () => {
  const module = await import('@/features/wizard/pages/FrameworkWizardPage');

  return { default: module.FrameworkWizardPage };
});

const PromptLibraryPage = lazy(async () => {
  const module = await import('@/features/library/pages/PromptLibraryPage');

  return { default: module.PromptLibraryPage };
});

const TextToPromptPage = lazy(async () => {
  const module = await import('@/features/generate/pages/TextToPromptPage');

  return { default: module.TextToPromptPage };
});

const PromptDetailPage = lazy(async () => {
  const module = await import('@/features/library/pages/PromptDetailPage');

  return { default: module.PromptDetailPage };
});

const PublicationAuthoringPage = lazy(async () => {
  const module = await import('@/features/publication/pages/PublicationAuthoringPage');

  return { default: module.PublicationAuthoringPage };
});

const TemplateCataloguePage = lazy(async () => {
  const module = await import('@/features/templates/pages/TemplateCataloguePage');

  return { default: module.TemplateCataloguePage };
});

/** The moderator's queue (TASK-0050): its own address, so a bookmark or a link to the queue is a real link. */
const PublicationReviewPage = lazy(async () => {
  const module = await import('@/features/admin/pages/PublicationReviewPage');

  return { default: module.PublicationReviewPage };
});

const TemplateDetailPage = lazy(async () => {
  const module = await import('@/features/templates/pages/TemplateDetailPage');

  return { default: module.TemplateDetailPage };
});

const AdminUsersPage = lazy(async () => {
  const module = await import('@/features/admin/pages/AdminUsersPage');

  return { default: module.AdminUsersPage };
});

const AdminAuthPolicyPage = lazy(async () => {
  const module = await import('@/features/admin/pages/AdminAuthPolicyPage');

  return { default: module.AdminAuthPolicyPage };
});

const AdminUserDetailPage = lazy(async () => {
  const module = await import('@/features/admin/pages/AdminUserDetailPage');

  return { default: module.AdminUserDetailPage };
});

const AdminDiscountsPage = lazy(async () => {
  const module = await import('@/features/admin/pages/AdminDiscountsPage');

  return { default: module.AdminDiscountsPage };
});

const StoreAccountPage = lazy(async () => {
  const module = await import('@/features/store/pages/StoreAccountPage');

  return { default: module.StoreAccountPage };
});

/**
 * The public store (TASK-0066). The shell is loaded with the store, not with the landing page: a visitor who never opens
 * the store never pays for its header, its discovery query or its menus.
 */
const StoreLayout = lazy(async () => {
  const module = await import('@/components/layout/StoreLayout');

  return { default: module.StoreLayout };
});

const StoreHomePage = lazy(async () => {
  const module = await import('@/features/store/pages/StoreHomePage');

  return { default: module.StoreHomePage };
});

const StorePromptListPage = lazy(async () => {
  const module = await import('@/features/store/pages/StorePromptListPage');

  return { default: module.StorePromptListPage };
});

const StorePromptDetailPage = lazy(async () => {
  const module = await import('@/features/store/pages/StorePromptDetailPage');

  return { default: module.StorePromptDetailPage };
});

const PublicProfilePage = lazy(async () => {
  const module = await import('@/features/store/pages/PublicProfilePage');

  return { default: module.PublicProfilePage };
});

/** The caller's cart (TASK-0072): loaded with the store, since a buyer reaches it from an entry page. */
const StoreCartPage = lazy(async () => {
  const module = await import('@/features/store/pages/StoreCartPage');

  return { default: module.StoreCartPage };
});

/** The receipt of one purchase (TASK-0072), addressed by the order the checkout answered with. */
const StoreOrderReceiptPage = lazy(async () => {
  const module = await import('@/features/store/pages/StoreOrderReceiptPage');

  return { default: module.StoreOrderReceiptPage };
});

/** Route tree rendered inside the browser router. */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />

      {/* The public store (TASK-0066). Deliberately **outside** `RequireAuth` and `RequireGuest`: a store is what a
          visitor with no account is shown, and a signed-in user browsing it must not be bounced to the dashboard either.
          The shell is the layout route, so every address beneath `/store` shares one header and one discovery read. */}
      <Route element={<StoreLayout />}>
        <Route path="/store" element={<StoreHomePage />} />
        {/* The listing is a real screen (TASK-0068); the entry page (TASK-0069) is addressed so that a shared URL and the
            cards' own links are real today, and only its content is deferred. */}
        <Route path="/store/prompts" element={<StorePromptListPage />} />
        <Route path="/store/prompts/:slug" element={<StorePromptDetailPage />} />
        {/* The public profile (TASK-0070) lives in the store's chrome for the same reason: it is read by visitors. */}
        <Route path="/profile/:username" element={<PublicProfilePage />} />
        {/* The cart and its receipts (TASK-0072) are account data, so they sit behind `RequireAuth` — but inside the store's
            chrome, because a buyer arrives from an entry page and leaves for one. */}
        <Route element={<RequireAuth />}>
          <Route path="/store/cart" element={<StoreCartPage />} />
          <Route path="/store/cart/orders/:orderId" element={<StoreOrderReceiptPage />} />
          {/* The account's own history (TASK-0073): purchases, Forge Credit movements and the codes it was given. It sits in
              the store's chrome like the cart, because a buyer arrives from a receipt and leaves for an entry. */}
          <Route path="/store/account" element={<StoreAccountPage />} />
        </Route>
      </Route>

      <Route element={<RequireGuest />}>
        <Route element={<AuthLayout />}>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          {/* The external sign-in comes back here (TASK-0062 §3.7): the API redirects the browser with an outcome code, not a token,
              and this page exchanges the ticket it carries. Outside `RequireGuest` because a signed-in owner also lands here — that
              is the `link-required` branch, which attaches a provider identity to the session that already exists. */}
          <Route path="/auth/external/callback" element={<ExternalCallbackPage />} />

          {/* The organisation authority returns the browser here (TASK-0064): the SPA's own OIDC client completes the PKCE flow
              on this address — it is the exact redirect URI the deployment registered — and exchanges the validated tokens for a
              session over a POST body. Like the external callback, a signed-in owner can land here too. */}
          <Route path="/auth/organisation/callback" element={<OrganisationCallbackPage />} />
        </Route>
      </Route>

      <Route element={<RequireAuth />}>
        {/* The verification screen a pending account finishes on (TASK-0062). Inside `RequireAuth` and **outside** the application
            shell's guard below, because it is the one protected address a pending account must be able to reach. */}
        <Route element={<AuthLayout />}>
          <Route path="/verify" element={<VerifyPage />} />
        </Route>
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<RequireVerifiedIdentity />}>
          <Route element={<AppShell />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            {/* TASK-0021: the questionnaire is no longer an entry point, so `/wizard` hands over to the framework wizard. The
              hand-over is a redirect rather than a screen, which is why it replaces the history entry it was reached by. */}
            <Route path="/wizard" element={<Navigate to="/wizard/framework" replace />} />
            {/* The wizard the product opens for a new prompt (TASK-0012): a static segment, so it wins over `/wizard/:sessionId`. */}
            <Route path="/wizard/framework" element={<FrameworkWizardPage />} />
            {/* Resume-only: the questionnaire of ADR-0009, kept while a draft written before TASK-0021 can still be reopened. */}
            <Route path="/wizard/:sessionId" element={<WizardPage />} />
            {/* The second way in: a raw text instead of the wizard (TASK-0010 §15–§18). */}
            <Route path="/generate" element={<TextToPromptPage />} />
            <Route path="/prompts" element={<PromptLibraryPage />} />
            {/* The workspace of one prompt (TASK-0044): the section is a *path segment* rather than local state, so a
              reload, a bookmark or a link pasted into a review returns to the same panel. An unknown segment falls back
              to `identity`, which is the address a bare `/prompts/{id}` also resolves to. */}
            <Route path="/prompts/:id/:section?" element={<PromptDetailPage />} />
            {/* Authoring one Prompt Store submission: the draft, its metadata, its body (TASK-0043). */}
            <Route path="/publications/:publicationId" element={<PublicationAuthoringPage />} />
            <Route path="/templates" element={<TemplateCataloguePage />} />
            {/* The catalogue addresses a template by identifier *or* slug, so the segment is deliberately unnamed. */}
            <Route path="/templates/:idOrSlug" element={<TemplateDetailPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />

            <Route element={<RequireAdministrator />}>
              <Route path="/admin" element={<AdminPage />} />
              {/* The moderation queue (TASK-0050) sits under the same guard as `/admin`: a moderator is an administrator,
                and the API refuses the endpoints to anybody else anyway. The segment is named for the resource rather
                than numbered, so the address says what it lists. */}
              <Route path="/admin/publication-review" element={<PublicationReviewPage />} />
              {/* Account administration (TASK-0061): the paged list and the detail of one account. The detail is addressed by
                identifier rather than by handle, because a handle can be renamed and a link that survives a rename is the
                only kind an operator can paste into a ticket. */}
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
              {/* The registration policy (TASK-0062): one screen, because the platform has one policy plus one override row. */}
              <Route path="/admin/auth-policy" element={<AdminAuthPolicyPage />} />
              {/* Discount codes (TASK-0073): issue, inspect, withdraw, assign and export them. */}
              <Route path="/admin/discounts" element={<AdminDiscountsPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
